import { DashStyles as Styles } from '../../components/dashboard/DashboardUI';
import { Constants } from '../../internal/Constants';
import { Localized } from '../../internal/Localized';
import { useTheme } from '../../providers/ThemeProvider';
import { Button, EngineConstants, Image, IsExpectedAuthError, Label, Textbox, useAuth } from '@app/engine';
import { Logger } from '@shared/common';
import { Telemetry } from '@shared/frontend';
import { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/** File-local constants. */
const localConstants = {
  logContext: 'DashboardLogin',
  telemetrySurfaceAuth: 'auth'
} as const;

/** View modes for the authentication screen. */
const AuthMode = {
  SignIn: 'signin',
  SignUp: 'signup',
  Forgot: 'forgot'
} as const;

type AuthModeValue = (typeof AuthMode)[keyof typeof AuthMode];

/**
 * Reads (and clears) the one-shot signup email handed over from the landing Get-Started flow.
 *
 * @returns The prefilled email, or an empty string when none was staged.
 */
function ConsumeSignupPrefillEmail(): string {
  const staged = sessionStorage.getItem(Constants.SignupPrefillStorageKey);
  if (!staged) return '';
  sessionStorage.removeItem(Constants.SignupPrefillStorageKey);
  return staged;
}

/**
 * Authentication screen shown when the visitor is not signed in.
 *
 * @returns The login screen element.
 */
export function Login() {
  const { signInWithEmailPassword, signUpWithEmailPassword, sendPasswordResetEmail } = useAuth();
  const { getThemedAssetDTO } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // The Vertex Echo wordmark (icon + product name baked in), so the auth card needs no separate title label.
  const logoAssetDTO = useMemo(() => getThemedAssetDTO(Constants.Bundle.Preload, 'Logo.png'), [getThemedAssetDTO]);
  // A one-shot prefill staged by the landing Get-Started flow starts the visitor in Create-account mode.
  const [prefillEmail] = useState(ConsumeSignupPrefillEmail);
  // Open directly in Create-account view when the landing flow staged an email OR the `?signup` deep link is present (the stable signup-screen URL handed to Twilio for the SMS opt-in notice).
  const [mode, setMode] = useState<AuthModeValue>(prefillEmail || searchParams.has(Constants.SignupQueryParam) ? AuthMode.SignUp : AuthMode.SignIn);
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Terms-of-service acceptance (sign-up only, required). SMS-consent acceptance (sign-up only, optional).
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [smsConsentAccepted, setSmsConsentAccepted] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [smsNoticeOpen, setSmsNoticeOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const shellRef = useRef<HTMLDivElement>(null);

  // Reveal the centred error message by scrolling the (overflowing) auth shell back to the top.
  useEffect(() => {
    if (error) shellRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [error]);

  const isSignUp = mode === AuthMode.SignUp;
  const isForgot = mode === AuthMode.Forgot;

  // Switches view mode, clearing any transient password input and feedback.
  const switchMode = (next: AuthModeValue) => {
    setError(null);
    setSuccess(null);
    setPassword('');
    setConfirmPassword('');
    setTermsAccepted(false);
    setSmsConsentAccepted(false);
    setTermsOpen(false);
    setSmsNoticeOpen(false);
    if (next === AuthMode.SignUp) Telemetry.SignUpStart();
    setMode(next);
  };

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (submitting) return;
    setError(null);
    setSuccess(null);

    if (isForgot) {
      if (!email) {
        setError(Localized.AuthEmailRequired);
        return;
      }
      setSubmitting(true);
      try {
        await sendPasswordResetEmail(email);
        Telemetry.PasswordResetSent();
        setSuccess(Localized.AuthResetSent);
      } catch (err: unknown) {
        // A rejected reset request (unknown/invalid email) is expected user input (Debug); only a system/abuse condition is a Warn.
        const message = Logger.GetErrorMessage(err);
        if (IsExpectedAuthError(Logger.GetErrorCode(err))) {
          Logger.Debug(localConstants.logContext, message);
        } else {
          Logger.Warn(localConstants.logContext, message);
        }
        Telemetry.SurfaceError(localConstants.telemetrySurfaceAuth, Logger.GetErrorCode(err));
        setError(Localized.AuthError);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (!email || !password) {
      setError(Localized.AuthFieldsRequired);
      return;
    }

    if (isSignUp && password !== confirmPassword) {
      setError(Localized.AuthPasswordMismatch);
      return;
    }

    // Terms-of-service gate: account creation requires explicit acceptance of the Terms of Service.
    if (isSignUp && !termsAccepted) {
      setError(Localized.AuthAgreeRequired);
      return;
    }

    setSubmitting(true);
    try {
      if (isSignUp) {
        await signUpWithEmailPassword(email, password, smsConsentAccepted);
        Telemetry.SignUpComplete();
      } else {
        await signInWithEmailPassword(email, password);
        Telemetry.Login();
      }
    } catch (err: unknown) {
      const code = Logger.GetErrorCode(err);
      // A wrong password, unknown user, or duplicate-email signup is expected user input (Debug); only a system/abuse condition (network, internal, too-many-requests) is a Warn.
      const message = Logger.GetErrorMessage(err);
      if (IsExpectedAuthError(code)) {
        Logger.Debug(localConstants.logContext, message);
      } else {
        Logger.Warn(localConstants.logContext, message);
      }
      Telemetry.SurfaceError(localConstants.telemetrySurfaceAuth, code);
      // A duplicate-email signup gets a specific, actionable message; everything else falls back to the generic auth error.
      setError(code === EngineConstants.AuthErrorEmailAlreadyInUse ? Localized.AuthErrorEmailInUse : Localized.AuthError);
    } finally {
      setSubmitting(false);
    }
  };

  const submitLabel = isForgot ? Localized.AuthSendReset : isSignUp ? Localized.SharedCreateAccount : Localized.AuthSubmitSignIn;

  return (
    <div className={Styles.authShell} ref={shellRef}>
      <div className={Styles.authCard}>
        <div className={Styles.authStack}>
          <div className={`${Styles.card} ${Styles.authPanel}`}>
            <Image
              title={Constants.Name}
              assetDTO={logoAssetDTO}
              classNameContainer={Styles.authTitleRow}
              classNameImageEnabled={Styles.brandWordmark}
              onClickHandler={() => navigate(Constants.Route.Landing)}
            />
          </div>

          <div className={`${Styles.card} ${Styles.authPanel}`}>
            <Label classNameContainer={Styles.authSubtitle} text={Localized.AuthSubtitle} />
            {error ? <Label classNameContainer={Styles.errorText} text={error} /> : null}
            {success ? <Label classNameContainer={Styles.successText} text={success} /> : null}
          </div>

          {isSignUp ? (
            <div className={`${Styles.card} ${Styles.authPanel}`}>
              <div className={`${Styles.field} ${Styles.consentRow}`}>
                {/* Raw <input>: checkbox needing checked and an id for the htmlFor label pairing; engine Textbox is a text input with no checked support. */}
                <input id="signup-terms" type="checkbox" className={Styles.consentCheckbox} checked={termsAccepted} onChange={(event) => setTermsAccepted(event.target.checked)} />
                <label htmlFor="signup-terms" className={Styles.consentLabel}>
                  {Localized.AuthAgreePrefix} <Button type="button" text={Localized.SharedTermsOfService} classNameButtonEnabled={Styles.consentLink} onClickHandler={() => setTermsOpen(true)} />
                </label>
              </div>
              <div className={`${Styles.field} ${Styles.consentRow}`}>
                {/* Raw <input>: checkbox needing checked and an id for the htmlFor label pairing; engine Textbox is a text input with no checked support. */}
                <input id="signup-sms" type="checkbox" className={Styles.consentCheckbox} checked={smsConsentAccepted} onChange={(event) => setSmsConsentAccepted(event.target.checked)} />
                <label htmlFor="signup-sms" className={Styles.consentLabel}>
                  {Localized.AuthSmsConsentPrefix} <Button type="button" text={Localized.SharedSmsNotice} classNameButtonEnabled={Styles.consentLink} onClickHandler={() => setSmsNoticeOpen(true)} />
                </label>
              </div>
            </div>
          ) : null}
        </div>

        <form onSubmit={submit} className={Styles.authForm}>
          <div className={`${Styles.card} ${Styles.authPanel}`}>
            <div className={Styles.field}>
              <Label classNameContainer={Styles.label} text={Localized.SharedEmail} />
              <Textbox type="email" autoComplete="email" value={email} onChange={(value) => setEmail(value)} classNameText={Styles.input} />
            </div>

            {!isForgot ? (
              <div className={Styles.field}>
                <Label classNameContainer={Styles.label} text={Localized.AuthPassword} />
                <Textbox type="password" autoComplete={isSignUp ? 'new-password' : 'current-password'} value={password} onChange={(value) => setPassword(value)} classNameText={Styles.input} />
              </div>
            ) : null}

            {isSignUp ? (
              <div className={Styles.field}>
                <Label classNameContainer={Styles.label} text={Localized.AuthPasswordConfirm} />
                <Textbox type="password" autoComplete="new-password" value={confirmPassword} onChange={(value) => setConfirmPassword(value)} classNameText={Styles.input} />
              </div>
            ) : null}
          </div>

          <div className={`${Styles.card} ${Styles.authPanel}`}>
            <Button
              text={submitting ? Localized.DashLoading : submitLabel}
              disabled={submitting}
              classNameButtonEnabled={`${Styles.btn} ${Styles.btnPrimary} ${Styles.btnBlock}`}
              classNameButtonDisabled={`${Styles.btn} ${Styles.btnPrimary} ${Styles.btnBlock}`}
            />

            {!isForgot ? (
              <Button
                text={isSignUp ? Localized.AuthToSignIn : Localized.AuthToSignUp}
                type="button"
                classNameButtonEnabled={`${Styles.navItem} ${Styles.authLink}`}
                onClickHandler={() => switchMode(isSignUp ? AuthMode.SignIn : AuthMode.SignUp)}
              />
            ) : null}

            {mode === AuthMode.SignIn ? (
              <Button text={Localized.AuthForgot} type="button" classNameButtonEnabled={`${Styles.navItem} ${Styles.authLinkTight}`} onClickHandler={() => switchMode(AuthMode.Forgot)} />
            ) : null}

            {isForgot ? (
              <Button text={Localized.AuthBackToSignIn} type="button" classNameButtonEnabled={`${Styles.navItem} ${Styles.authLink}`} onClickHandler={() => switchMode(AuthMode.SignIn)} />
            ) : null}
          </div>
        </form>
      </div>

      {termsOpen ? (
        // Terms of Service panel over the form. Click-outside or Close dismisses; the content scrolls inside the panel on small viewports.
        <div className={Styles.termsOverlay} onClick={() => setTermsOpen(false)}>
          <div className={`${Styles.card} ${Styles.termsPanel}`} role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <Label classNameContainer={`${Styles.sectionTitle} ${Styles.termsTitle}`} text={Localized.SharedTermsOfService} />
            <div className={Styles.termsBody}>
              <Label classNameContainer={Styles.termsParagraph} text={Localized.AuthTermsIntro} />
              <Label classNameContainer={`${Styles.label} ${Styles.termsHeading}`} text={Localized.AuthTermsPrivacyTitle} />
              <Label classNameContainer={Styles.termsParagraph} text={Localized.AuthTermsPrivacyBody} />
            </div>
            <Button text={Localized.SharedClose} type="button" classNameButtonEnabled={`${Styles.btn} ${Styles.btnBlock}`} onClickHandler={() => setTermsOpen(false)} />
          </div>
        </div>
      ) : null}

      {smsNoticeOpen ? (
        // SMS Notice panel over the form. Click-outside or Close dismisses; the content scrolls inside the panel on small viewports.
        <div className={Styles.termsOverlay} onClick={() => setSmsNoticeOpen(false)}>
          <div className={`${Styles.card} ${Styles.termsPanel}`} role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <Label classNameContainer={`${Styles.sectionTitle} ${Styles.termsTitle}`} text={Localized.SharedSmsNotice} />
            <div className={Styles.termsBody}>
              <Label classNameContainer={Styles.termsParagraph} text={Localized.AuthSmsNoticeIntro} />
              <Label classNameContainer={`${Styles.label} ${Styles.termsHeading}`} text={Localized.AuthTermsSmsTitle} />
              <Label classNameContainer={Styles.termsParagraph} text={Localized.AuthTermsSmsBody} />
              <Label classNameContainer={Styles.termsParagraph} text={Localized.AuthTermsSmsOptOut} />
              <Label classNameContainer={`${Styles.label} ${Styles.termsHeading}`} text={Localized.AuthTermsCallersTitle} />
              <Label classNameContainer={Styles.termsParagraph} text={Localized.AuthTermsCallersBody} />
            </div>
            <Button text={Localized.SharedClose} type="button" classNameButtonEnabled={`${Styles.btn} ${Styles.btnBlock}`} onClickHandler={() => setSmsNoticeOpen(false)} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
